// #564 Einsatz-Tracking (T2): Liste mit Filter Offen · In Arbeit · Erledigt und Detail mit Zeitleiste,
// Fotos vorher/nachher und Kosten (abrechenbare Zeit × Stundensatz, Material). Nur lesen, nichts verschicken.
import { useEffect, useState } from 'react';
import { ArrowLeft, CaretRight, ChatCircleDots, ChatCircleText, Check, CircleNotch, Coins, Images, MapPin, Wrench, X } from '@phosphor-icons/react';
import NachrichtenVerlauf from '../../components/portal/NachrichtenVerlauf';
import type { PortalNachricht } from '../../lib/portal/nachrichtenApi';
import { FehlerHinweis, IconChip, LeerZustand, SegmentSteuerung, StatusChip, type StatusTon } from '../../components/portal/bausteine';
import {
  STUFE_TEXT, anfrageBeantwortet, anfrageStatusText, anzahlJeFilter, einsatzStufe, einsaetzeFuer, kostenBrutto, zeitText, zeitleiste,
  type EinsatzDetail, type EinsatzFilter, type EinsatzKurz, type EinsatzStufe, type KostenStand,
} from '../../lib/portal/einsaetze';
import { MIN_BEGRUENDUNG, begruendungOk } from '../../lib/portal/eigentuemerE7';

const STUFE_TON: Record<EinsatzStufe, StatusTon> = {
  gemeldet: 'neutral', in_arbeit: 'warn', wartet_handwerker: 'info', wartet_rueckmeldung: 'info', erledigt: 'ok',
};
const euro = (n: number) => n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
const DATUM = (iso: string) => new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

export interface EinsatzQuelle {
  detail: (id: string) => Promise<EinsatzDetail | null>;
  foto: (id: string) => Promise<string>;
  nachrichten: (id: string) => Promise<PortalNachricht[]>;
  senden: (id: string, text: string) => Promise<void>;
  /** T4 Kostenfreigabe */
  kostenStand: (id: string) => Promise<KostenStand | null>;
  kostenEntscheiden: (id: string, e: 'freigegeben' | 'abgelehnt', grund?: string) => Promise<void>;
}

/** T4: Kostenfreigabe im Vorgang – Freigeben oder mit Grund (≥ 5 Zeichen) ablehnen. */
function KostenKarte({ id, quelle, onGeaendert, erledigt }: { id: string; quelle: EinsatzQuelle; onGeaendert: () => void; erledigt: boolean }) {
  const [stand, setStand] = useState<KostenStand | null | undefined>(undefined);
  const [ablehnen, setAblehnen] = useState(false);
  const [grund, setGrund] = useState('');
  const [grundFehler, setGrundFehler] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  useEffect(() => { let a = true; void quelle.kostenStand(id).then((s) => { if (a) setStand(s); }); return () => { a = false; }; }, [id, quelle]);
  if (!stand) return null;
  const betragText = euro(Number(stand.betrag) || 0);
  const entscheide = async (e: 'freigegeben' | 'abgelehnt') => {
    if (e === 'abgelehnt' && !begruendungOk(grund)) { setGrundFehler(true); return; }
    setLaeuft(true); setFehler(null);
    try {
      await quelle.kostenEntscheiden(id, e, e === 'abgelehnt' ? grund : undefined);
      setStand({ ...stand, stand: e, am: new Date().toISOString(), begruendung: e === 'abgelehnt' ? grund.trim() : null });
      onGeaendert();
    } catch (x) { setFehler((x as Error).message); } finally { setLaeuft(false); }
  };
  const wartet = stand.stand === 'wartet';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 14, borderRadius: 18,
      background: wartet ? 'var(--p-ton-warn-bg)' : stand.stand === 'freigegeben' ? 'var(--p-ton-ok-bg)' : 'var(--p-ton-fehler-bg)' }} aria-label="Kostenfreigabe">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Coins size={20} weight="fill" style={{ color: wartet ? 'var(--p-ton-warn-fg)' : stand.stand === 'freigegeben' ? 'var(--p-ton-ok-fg)' : 'var(--p-ton-fehler-fg)' }} />
        <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontSize: 14.5, fontWeight: 800 }}>{wartet ? 'Kostenfreigabe' : stand.stand === 'freigegeben' ? 'Von Ihnen freigegeben' : 'Von Ihnen abgelehnt'}</span>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--p-w60)' }}>Geschätzte Kosten {betragText} brutto{stand.am ? ` · ${DATUM(stand.am)}` : ''}</span>
        </span>
        {wartet && <StatusChip ton="warn">Wartet auf Sie</StatusChip>}
      </div>
      {!wartet && stand.begruendung && <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--p-w75)' }}>„{stand.begruendung}“</span>}
      {stand.stand === 'abgelehnt' && !erledigt && (
        <>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--p-w60)' }}>Bis das geklärt ist, wird niemand beauftragt. Die Verwaltung meldet sich bei Ihnen.</span>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button type="button" className="p-knopf" disabled={laeuft} data-tip="Eine Rückfrage zu diesem Einsatz an die Verwaltung schreiben"
              onClick={() => { const f = document.querySelector<HTMLTextAreaElement>('textarea[aria-label="Rückfrage an die Verwaltung"]'); f?.scrollIntoView({ behavior: 'smooth', block: 'center' }); f?.focus(); }}
              style={{ height: 40, border: 'none', background: 'var(--p-w08)', fontSize: 13 }}>
              <ChatCircleDots size={16} weight="bold" /> Rückfrage
            </button>
            <button type="button" className="p-knopf p-knopf-gruen" onClick={() => void entscheide('freigegeben')} disabled={laeuft}
              data-tip="Ablehnung zurücknehmen und die Kosten doch freigeben" style={{ height: 40, fontSize: 13 }}>
              {laeuft ? <CircleNotch size={16} weight="bold" className="animate-spin" /> : <Check size={16} weight="bold" />} Doch freigeben
            </button>
          </div>
        </>
      )}
      {wartet && ablehnen && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <textarea value={grund} autoFocus rows={3} maxLength={1000} aria-label="Grund für die Ablehnung"
            onChange={(e) => { setGrund(e.target.value); if (grundFehler && begruendungOk(e.target.value)) setGrundFehler(false); }}
            placeholder="Grund, z. B. „Bitte zweites Angebot einholen“"
            style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', borderRadius: 14, padding: '10px 12px', fontSize: 13.5, fontWeight: 600, fontFamily: 'inherit',
              color: 'var(--p-w)', background: 'var(--p-w05)', border: 'none', boxShadow: grundFehler ? 'var(--p-fehler-ring)' : 'inset 0 0 0 1px var(--p-w08)' }} />
          {grundFehler && <span role="alert" style={{ fontSize: 12, fontWeight: 700, color: 'var(--p-fehler-text)' }}>Bitte geben Sie einen Grund an (mindestens {MIN_BEGRUENDUNG} Zeichen).</span>}
        </div>
      )}
      {fehler && <FehlerHinweis text={fehler} onSchliessen={() => setFehler(null)} />}
      {wartet && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          {!ablehnen ? (
            <>
              <button type="button" className="p-knopf" onClick={() => setAblehnen(true)} disabled={laeuft} data-tip="Kosten ablehnen und Grund angeben"
                style={{ height: 40, border: 'none', background: 'var(--p-ton-fehler-bg)', color: 'var(--p-ton-fehler-fg)', fontSize: 13 }}>Ablehnen</button>
              <button type="button" className="p-knopf p-knopf-gruen" onClick={() => void entscheide('freigegeben')} disabled={laeuft} data-tip="Kosten für diesen Einsatz freigeben" style={{ height: 40, fontSize: 13 }}>
                {laeuft ? <CircleNotch size={16} weight="bold" className="animate-spin" /> : <Check size={16} weight="bold" />} Freigeben
              </button>
            </>
          ) : (
            <>
              <button type="button" className="p-knopf" onClick={() => { setAblehnen(false); setGrund(''); setGrundFehler(false); }} disabled={laeuft}
                data-tip="Zurück, ohne abzulehnen" style={{ height: 40, border: 'none', background: 'var(--p-w08)', fontSize: 13 }}>Zurück</button>
              <button type="button" className="p-knopf" onClick={() => void entscheide('abgelehnt')} disabled={laeuft} data-tip="Ablehnung mit Begründung an die Verwaltung senden"
                style={{ height: 40, border: 'none', background: 'var(--p-notfall)', color: 'var(--p-w)', fontSize: 13 }}>
                {laeuft ? <CircleNotch size={16} weight="bold" className="animate-spin" /> : <X size={16} weight="bold" />} Ablehnen
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Foto({ id, art, quelle, onGross }: { id: string; art: 'vorher' | 'nachher'; quelle: EinsatzQuelle; onGross: (url: string) => void }) {
  const [url, setUrl] = useState<string | null>(null);
  const [fehler, setFehler] = useState(false);
  useEffect(() => {
    let aktiv = true; let lokal: string | null = null;
    quelle.foto(id).then((u) => { lokal = u; if (aktiv) setUrl(u); else URL.revokeObjectURL(u); }).catch(() => { if (aktiv) setFehler(true); });
    return () => { aktiv = false; if (lokal) URL.revokeObjectURL(lokal); };
  }, [id, quelle]);
  const text = art === 'vorher' ? 'Foto vorher' : 'Foto nachher';
  return (
    <button type="button" onClick={() => url && onGross(url)} disabled={!url} data-tip={`${text} vergrößern`} aria-label={`${text} vergrößern`}
      style={{ position: 'relative', aspectRatio: '4 / 3', borderRadius: 16, overflow: 'hidden', border: 'none', padding: 0, cursor: url ? 'zoom-in' : 'default', background: 'var(--p-w05)' }}>
      {url && <img src={url} alt={text} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
      {!url && !fehler && <CircleNotch size={20} weight="bold" className="animate-spin" style={{ color: 'var(--p-w40)' }} />}
      {fehler && <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--p-w50)' }}>Foto nicht verfügbar</span>}
      <span style={{ position: 'absolute', left: 8, bottom: 8, padding: '3px 9px', borderRadius: 9, fontSize: 11, fontWeight: 800,
        background: art === 'vorher' ? 'var(--p-ton-warn-bg)' : 'var(--p-ton-ok-bg)', color: art === 'vorher' ? 'var(--p-ton-warn-fg)' : 'var(--p-ton-ok-fg)' }}>{text}</span>
    </button>
  );
}

function Detail({ e, quelle, onZurueck, onGeaendert }: { e: EinsatzKurz; quelle: EinsatzQuelle; onZurueck: () => void; onGeaendert: () => void }) {
  const [d, setD] = useState<EinsatzDetail | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [gross, setGross] = useState<string | null>(null);
  useEffect(() => {
    let aktiv = true;
    void quelle.detail(e.id).then((x) => { if (aktiv) { setD(x); setLaedt(false); } });
    return () => { aktiv = false; };
  }, [e.id, quelle]);
  useEffect(() => {
    if (!gross) return;
    const taste = (k: KeyboardEvent) => { if (k.key === 'Escape') setGross(null); };
    document.addEventListener('keydown', taste);
    return () => document.removeEventListener('keydown', taste);
  }, [gross]);

  const stufe = einsatzStufe(d?.status ?? e.status);
  const k = d ? kostenBrutto(d) : { zeilen: [], summe: 0, netto: 0 };
  const anfrage = e.art === 'anfrage';
  const punkte = d ? zeitleiste(d).map((p, i) => (i === 0 && anfrage ? { ...p, text: 'Ihre Anfrage' } : p)) : [];

  return (
    <div className="p-rein" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <button type="button" onClick={onZurueck} data-tip="Zurück zu allen Vorgängen" className="p-knopf"
        style={{ alignSelf: 'flex-start', height: 38, border: 'none', background: 'var(--p-w08)', fontSize: 13 }}>
        <ArrowLeft size={15} weight="bold" /> Alle Vorgänge
      </button>
      <section className="p-glas p-farbe-orange" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 16 }} aria-label={e.titel}>
        <div className="p-glas-schimmer" />
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.015em', overflowWrap: 'anywhere' }}>{e.titel}</span>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--p-w50)' }}>
              {[e.einheit, e.ort?.split(' · ').pop(), e.ticket_nummer ? `Ticket ${e.ticket_nummer}` : null].filter(Boolean).join(' · ')}
            </span>
          </span>
          {anfrage
            ? <StatusChip ton={anfrageBeantwortet({ status: d?.status ?? e.status, beantwortet: e.beantwortet }) ? 'ok' : 'neutral'} gross>{anfrageStatusText({ status: d?.status ?? e.status, beantwortet: e.beantwortet })}</StatusChip>
            : <StatusChip ton={STUFE_TON[stufe]} gross>{STUFE_TEXT[stufe]}</StatusChip>}
        </div>

        {laedt && <div style={{ display: 'flex', justifyContent: 'center', padding: 20, color: 'var(--p-w40)' }} aria-busy="true"><CircleNotch size={22} weight="bold" className="animate-spin" /></div>}
        {!laedt && !d && <FehlerHinweis text="Dieser Vorgang konnte nicht geladen werden." />}

        {d && <KostenKarte id={e.id} quelle={quelle} onGeaendert={onGeaendert} erledigt={stufe === 'erledigt'} />}

        {d?.beschreibung && <p style={{ margin: 0, fontSize: 13.5, fontWeight: 600, lineHeight: 1.5, color: 'var(--p-w75)', whiteSpace: 'pre-line' }}>{d.beschreibung}</p>}

        {punkte.length > 0 && (
          <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column' }} aria-label="Verlauf">
            {punkte.map((p, i) => (
              <li key={`${p.zeit}-${p.text}`} style={{ display: 'grid', gridTemplateColumns: '18px 1fr', gap: 12 }}>
                <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <span style={{ width: 12, height: 12, borderRadius: 6, marginTop: 4, flexShrink: 0,
                    background: p.zukunft ? 'transparent' : 'var(--p-schalter-an)', boxShadow: p.zukunft ? 'inset 0 0 0 2px var(--p-w40)' : 'none' }} />
                  {i < punkte.length - 1 && <span style={{ flex: 1, width: 2, minHeight: 18, background: p.zukunft ? 'var(--p-w14)' : 'var(--p-ton-ok-bg)' }} />}
                </span>
                <span style={{ display: 'flex', flexDirection: 'column', gap: 1, paddingBottom: 12, minWidth: 0 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: p.zukunft ? 'var(--p-w60)' : 'var(--p-w)', overflowWrap: 'anywhere' }}>{p.text}</span>
                  {!p.text.startsWith('Termin am') && <span className="p-zahl" style={{ fontSize: 12, fontWeight: 600, color: 'var(--p-w45)' }}>{zeitText(p.zeit)}</span>}
                </span>
              </li>
            ))}
          </ol>
        )}

        {d && d.fotos.length > 0 && (
          <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 220px), 1fr))' }}>
            {d.fotos.map((f) => <Foto key={f.id} id={f.id} art={f.art} quelle={quelle} onGross={setGross} />)}
          </div>
        )}

        {k.zeilen.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 4 }} aria-label="Kosten">
            {k.zeilen.map((z) => (
              <div key={z.text} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13.5, fontWeight: 600, color: 'var(--p-w75)' }}>
                <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{z.text}</span>
                <span className="p-zahl" style={{ whiteSpace: 'nowrap' }}>{euro(z.betrag)}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, paddingTop: 8, borderTop: '1px solid var(--p-w08)', fontSize: 14.5, fontWeight: 800 }}>
              <span>Summe brutto</span><span className="p-zahl">{euro(k.summe)}</span>
            </div>
            <span className="p-zahl" style={{ alignSelf: 'flex-end', fontSize: 12, fontWeight: 600, color: 'var(--p-w50)' }}>netto {euro(k.netto)} · inkl. 19 % USt.</span>
          </div>
        )}
        {d && <NachrichtenVerlauf ticketId={e.id} laden={quelle.nachrichten} senden={quelle.senden} />}
      </section>

      {gross && (
        <div role="dialog" aria-label="Foto" onClick={() => setGross(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'var(--p-sheet-schleier)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <button type="button" onClick={() => setGross(null)} data-tip="Schließen" aria-label="Foto schließen" className="p-rund"
            style={{ position: 'absolute', top: 'calc(16px + env(safe-area-inset-top, 0px))', right: 16, background: 'var(--p-w14)', color: 'var(--p-w)' }}>
            <X size={18} weight="bold" />
          </button>
          <img src={gross} alt="Foto zum Einsatz" style={{ maxWidth: '100%', maxHeight: '90vh', borderRadius: 18, objectFit: 'contain' }} onClick={(x) => x.stopPropagation()} />
        </div>
      )}
    </div>
  );
}

export default function EigentuemerEinsaetze({ einsaetze, objektId, quelle, staende = {}, onGeaendert }: {
  einsaetze: EinsatzKurz[]; objektId: string; quelle: EinsatzQuelle;
  /** T4: Stand der Kostenfreigabe je Vorgang */
  staende?: Record<string, KostenStand | null>;
  onGeaendert?: () => void;
}) {
  const zahlen = anzahlJeFilter(einsaetze, objektId);
  const [filter, setFilter] = useState<EinsatzFilter>(zahlen.offen ? 'offen' : zahlen.in_arbeit ? 'in_arbeit' : 'erledigt');
  const [offen, setOffen] = useState<EinsatzKurz | null>(null);
  useEffect(() => { setOffen(null); }, [objektId]);

  if (offen) return <Detail e={offen} quelle={quelle} onGeaendert={() => onGeaendert?.()} onZurueck={() => { setOffen(null); window.scrollTo(0, 0); }} />;
  const liste = einsaetzeFuer(einsaetze, objektId, filter);

  return (
    <div className="p-rein" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ maxWidth: 520 }}>
        <SegmentSteuerung label="Vorgänge filtern" wert={filter} onWahl={setFilter} hoehe={40}
          optionen={[
            { key: 'offen', label: `Offen (${zahlen.offen})`, tip: 'Gemeldet, noch nicht begonnen' },
            { key: 'in_arbeit', label: `In Arbeit (${zahlen.in_arbeit})`, tip: 'In Arbeit oder wartet auf Handwerker bzw. Rückmeldung' },
            { key: 'erledigt', label: `Erledigt (${zahlen.erledigt})`, tip: 'Abgeschlossene Einsätze' },
          ]} />
      </div>
      {liste.length === 0 ? <LeerZustand icon={Wrench} text="Keine Vorgänge in dieser Ansicht" /> : (
        <div className="mp-karten">
          {liste.map((e) => {
            const stufe = einsatzStufe(e.status);
            return (
              <button key={e.id} type="button" onClick={() => { setOffen(e); window.scrollTo(0, 0); }} data-tip="Vorgang mit Verlauf, Fotos, Kosten und Nachrichten öffnen"
                className="p-glas p-hebt p-farbe-orange" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10, textAlign: 'left', color: 'var(--p-w)', border: 'none', cursor: 'pointer', minWidth: 0 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, width: '100%' }}>
                  <IconChip farbe={e.art === 'anfrage' ? 'gemein' : stufe === 'erledigt' ? 'konto' : 'orange'} icon={e.art === 'anfrage' ? ChatCircleText : Wrench} groesse={38} />
                  <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <span className="p-kuerzen" style={{ fontSize: 15, fontWeight: 800 }}>{e.titel}</span>
                    <span className="p-kuerzen" style={{ fontSize: 12, fontWeight: 600, color: 'var(--p-w50)' }}>
                      {[e.einheit, e.ticket_nummer, `gemeldet am ${DATUM(e.created_at)}`].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  <CaretRight size={14} weight="bold" style={{ color: 'var(--p-w35)', flexShrink: 0 }} />
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {e.art === 'anfrage' && <StatusChip ton="info">Anfrage</StatusChip>}
                  {staende[e.id]?.stand === 'wartet' && <StatusChip ton="warn">Wartet auf Ihre Freigabe</StatusChip>}
                  {e.art === 'anfrage'
                    ? <StatusChip ton={anfrageBeantwortet(e) ? 'ok' : 'neutral'}>{anfrageStatusText(e)}</StatusChip>
                    : <StatusChip ton={STUFE_TON[stufe]}>{STUFE_TEXT[stufe]}</StatusChip>}
                  {!!e.nachrichten && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--p-w50)' }}><ChatCircleDots size={13} />{e.nachrichten}</span>}
                  {e.ort && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--p-w50)' }}><MapPin size={13} />{e.ort.split(' · ').pop()}</span>}
                  {e.fotos > 0 && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--p-w50)' }}><Images size={13} />{e.fotos} {e.fotos === 1 ? 'Foto' : 'Fotos'}</span>}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
